import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useIG } from "../lib/igContext";
import { TrendingUp, Users, Eye, Heart, MessageSquare, Bookmark, Globe, Plus, Loader2, X, Calendar, Activity } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { id: "7", label: "7j" }, { id: "30", label: "30j" },
  { id: "90", label: "90j" }, { id: "365", label: "1 an" },
];

export default function AnalyticsPro() {
  const { selected, isGlobal, accounts, loading } = useIG();
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allStats, setAllStats] = useState({});  // keyed by account_id
  const [days, setDays] = useState("30");
  const [showManual, setShowManual] = useState(false);

  const refresh = async () => {
    if (loading) return;
    if (selected) {
      try {
        const [s, sum] = await Promise.all([
          api.igStats(selected.id, parseInt(days)),
          api.igStatsSummary(selected.id),
        ]);
        setStats(s); setSummary(sum);
      } catch { toast.error("Erreur stats"); }
    } else {
      // Global view: load all accounts stats
      const map = {};
      for (const a of accounts) {
        try { map[a.id] = await api.igStats(a.id, parseInt(days)); } catch {}
      }
      setAllStats(map);
    }
  };

  useEffect(() => { refresh(); }, [selected?.id, days, loading, accounts.length]);

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="analytics-pro-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// ANALYTICS INSTAGRAM</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">{isGlobal ? "Vue globale" : selected?.display_name || `@${selected?.handle}`}</h1>
          <p className="text-zinc-400 mt-2">Historisation jour par jour, comparaisons, courbes, deltas.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="border border-[#1E1E24] rounded-sm flex overflow-hidden">
            {RANGES.map(r => (
              <button key={r.id} data-testid={`range-${r.id}`} onClick={() => setDays(r.id)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition ${
                  days === r.id ? "bg-[#CCFF00] text-black" : "text-zinc-400 hover:text-white"
                }`}>{r.label}</button>
            ))}
          </div>
          {selected && (
            <button onClick={() => setShowManual(true)} data-testid="manual-stats-btn"
              className="text-xs bg-[#CCFF00] text-black font-medium px-3 py-1.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Saisir stats du jour
            </button>
          )}
        </div>
      </header>

      {!selected && accounts.length === 0 && (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Activity className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucun compte Instagram ajouté.</div>
        </div>
      )}

      {selected && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
            <Card icon={Users} label="Abonnés actuels" v={summary.today?.followers ?? 0} delta={summary.delta_followers_24h} />
            <Card icon={TrendingUp} label="Nouveaux 7j" v={summary.new_followers_7d} accent="#CCFF00" />
            <Card icon={TrendingUp} label="Nouveaux 30j" v={summary.new_followers_30d} accent="#CCFF00" />
            <Card icon={Eye} label="Reach 30j" v={summary.total_reach_30d.toLocaleString("fr-FR")} />
            <Card icon={Eye} label="Impressions 30j" v={summary.total_impressions_30d.toLocaleString("fr-FR")} />
            <Card icon={Heart} label="Engagement moy. 30j" v={summary.avg_engagement_30d + "%"} />
            <Card icon={MessageSquare} label="Commentaires aujourd'hui" v={summary.today?.comments ?? 0} />
            <Card icon={Bookmark} label="Saves aujourd'hui" v={summary.today?.saves ?? 0} />
          </div>

          {summary.best_day && (
            <div className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.04] rounded-sm p-5">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00] mb-2">// MEILLEUR JOUR HISTORIQUE</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-[10px] font-mono text-zinc-500">DATE</div><div className="text-white font-mono">{summary.best_day.date}</div></div>
                <div><div className="text-[10px] font-mono text-zinc-500">REACH</div><div className="text-white font-mono">{summary.best_day.reach.toLocaleString("fr-FR")}</div></div>
                <div><div className="text-[10px] font-mono text-zinc-500">FOLLOWERS</div><div className="text-white font-mono">{summary.best_day.followers}</div></div>
                <div><div className="text-[10px] font-mono text-zinc-500">ENGAGEMENT</div><div className="text-white font-mono">{summary.best_day.engagement_rate}%</div></div>
              </div>
            </div>
          )}

          <SparkChart title="Évolution abonnés" data={stats} field="followers" color="#CCFF00" />
          <SparkChart title="Évolution reach" data={stats} field="reach" color="#00D4FF" />
          <SparkChart title="Engagement rate (%)" data={stats} field="engagement_rate" color="#A78BFA" />

          {stats.length === 0 && (
            <div className="border border-dashed border-[#1E1E24] rounded-sm p-10 text-center">
              <div className="text-zinc-500 text-sm mb-3">Aucune donnée historisée pour ce compte.</div>
              <button onClick={() => setShowManual(true)} className="text-[#CCFF00] text-sm font-mono uppercase tracking-wider hover:underline">
                + Saisir tes premières stats
              </button>
            </div>
          )}
        </>
      )}

      {isGlobal && accounts.length > 0 && (
        <section className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">// COMPARAISON COMPTES</div>
          <div className="border border-[#1E1E24] rounded-sm overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#0A0A0C] text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              <div className="col-span-4">Compte</div>
              <div className="col-span-2 text-right">Abonnés</div>
              <div className="col-span-2 text-right">Reach 30j</div>
              <div className="col-span-2 text-right">Δ 7j</div>
              <div className="col-span-2 text-right">Statut</div>
            </div>
            {accounts.map(a => {
              const s = allStats[a.id] || [];
              const reach30 = s.slice(-30).reduce((acc, r) => acc + (r.reach || 0), 0);
              const delta7 = s.length >= 8 ? (s[s.length - 1].followers - s[s.length - 8].followers) : 0;
              return (
                <div key={a.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#1E1E24] items-center hover:bg-white/[0.02]">
                  <div className="col-span-4">
                    <div className="text-white text-sm">{a.display_name || a.handle}</div>
                    <div className="text-[10px] font-mono text-zinc-500">@{a.handle}</div>
                  </div>
                  <div className="col-span-2 text-right font-mono text-sm text-white">{(a.followers || 0).toLocaleString("fr-FR")}</div>
                  <div className="col-span-2 text-right font-mono text-sm text-zinc-300">{reach30.toLocaleString("fr-FR")}</div>
                  <div className={`col-span-2 text-right font-mono text-sm ${delta7 > 0 ? "text-green-400" : delta7 < 0 ? "text-red-400" : "text-zinc-500"}`}>
                    {delta7 > 0 ? "+" : ""}{delta7}
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-zinc-700/30 text-zinc-400">{a.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {showManual && selected && <ManualStatsModal account={selected} onClose={() => setShowManual(false)} onSuccess={() => { setShowManual(false); refresh(); }} />}
    </div>
  );
}

const Card = ({ icon: I, label, v, accent, delta }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
    <div className="flex items-center gap-2 mb-2">
      <I className="w-3.5 h-3.5" style={{ color: accent || "#888" }} strokeWidth={1.5} />
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
    <div className="font-mono text-2xl font-bold" style={{ color: accent || "#FFF" }}>{v}</div>
    {delta != null && (
      <div className={`text-[10px] font-mono mt-1 ${delta > 0 ? "text-green-400" : delta < 0 ? "text-red-400" : "text-zinc-500"}`}>
        {delta > 0 ? "+" : ""}{delta} vs hier
      </div>
    )}
  </div>
);

function SparkChart({ title, data, field, color }) {
  const values = data.map(d => d[field] || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;

  if (data.length < 2) return null;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - (((d[field] || 0) - min) / range) * 100;
    return `${x},${y}`;
  }).join(" ");

  const last = values[values.length - 1];
  const first = values[0];
  const delta = last - first;
  const deltaPct = first ? Math.round((delta / first) * 100) : 0;

  return (
    <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">// {title.toUpperCase()}</div>
        <div className={`text-xs font-mono ${delta > 0 ? "text-green-400" : delta < 0 ? "text-red-400" : "text-zinc-500"}`}>
          {delta > 0 ? "+" : ""}{delta} ({deltaPct}%)
        </div>
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-32">
        <polyline points={points} fill="none" stroke={color} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-zinc-600">
        <span>{data[0]?.date}</span>
        <span className="font-mono text-base text-white">{last.toLocaleString("fr-FR")}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function ManualStatsModal({ account, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    date: today, followers: account.followers || 0, posts_count: account.posts_count || 0,
    reach: 0, impressions: 0, profile_visits: 0, website_clicks: 0,
    reel_views: 0, likes: 0, comments: 0, saves: 0, shares: 0, engagement_rate: 0, new_followers: 0,
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const data = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, k === "date" ? v : Number(v)]));
      await api.igAddManualStats({ account_id: account.id, ...data });
      toast.success("Stats enregistrées"); onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-stats-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl text-white">Stats du jour — @{account.handle}</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Remplis ce que tu as dans Instagram Insights. La progression se calcule automatiquement vs la veille.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <NumF label="Date" type="date" v={f.date} on={v => setF({...f, date: v})} testid="ms-date" />
          <NumF label="Abonnés" v={f.followers} on={v => setF({...f, followers: v})} testid="ms-followers" />
          <NumF label="Posts total" v={f.posts_count} on={v => setF({...f, posts_count: v})} testid="ms-posts" />
          <NumF label="Reach" v={f.reach} on={v => setF({...f, reach: v})} testid="ms-reach" />
          <NumF label="Impressions" v={f.impressions} on={v => setF({...f, impressions: v})} />
          <NumF label="Profile visits" v={f.profile_visits} on={v => setF({...f, profile_visits: v})} />
          <NumF label="Website clicks" v={f.website_clicks} on={v => setF({...f, website_clicks: v})} />
          <NumF label="Vues Reels" v={f.reel_views} on={v => setF({...f, reel_views: v})} />
          <NumF label="Likes" v={f.likes} on={v => setF({...f, likes: v})} />
          <NumF label="Commentaires" v={f.comments} on={v => setF({...f, comments: v})} />
          <NumF label="Saves" v={f.saves} on={v => setF({...f, saves: v})} />
          <NumF label="Shares" v={f.shares} on={v => setF({...f, shares: v})} />
          <NumF label="Engagement rate %" v={f.engagement_rate} on={v => setF({...f, engagement_rate: v})} step="0.1" />
          <NumF label="Nouveaux followers" v={f.new_followers} on={v => setF({...f, new_followers: v})} />
        </div>

        <button type="submit" disabled={loading} data-testid="ms-submit"
          className="w-full mt-5 bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "Enregistrement..." : "Enregistrer la journée"}
        </button>
      </form>
    </div>
  );
}

const NumF = ({ label, v, on, type = "number", step, testid }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <input data-testid={testid} type={type} step={step || "1"} value={v} onChange={e => on(e.target.value)}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
  </div>
);
